/**
 * SKELETON RETARGETING — Mixamo clips onto a non-Mixamo rig.
 *
 * The problem this exists to solve: the enemies that look right (the
 * Quaternius horde, coloured and textured) ship exactly ONE animation
 * each, and the 50-clip Mixamo combat library that would give them
 * impacts, deaths and attacks is authored for a completely different
 * skeleton. Grafting by bone name — which is what build-assets does
 * between two Mixamo rigs — binds zero channels here, because not one
 * bone name is shared.
 *
 * WHY THE NAIVE VERSION FAILS. A rotation track is a LOCAL rotation, and
 * a local rotation only means something relative to the rest pose and the
 * bone axes it was authored against. Mixamo rests in a T-pose with its own
 * axis convention; Quaternius does not. Copying `LeftArm.quaternion`
 * straight onto `UpperArm.L` produces a body with its arms through its
 * chest — the classic broken retarget.
 *
 * WHAT THIS DOES INSTEAD. Every bone is taken to WORLD space, where
 * "rotate the upper arm down by sixty degrees" means the same thing on any
 * rig regardless of naming, rest pose or axis convention:
 *
 *   1. delta   = sourceWorld(t) * sourceRestWorld⁻¹      (what the clip DID)
 *   2. want    = delta * targetRestWorld                 (do it to this rig)
 *   3. local   = parentWant⁻¹ * want                     (back to a track)
 *
 * Unmapped target bones (fingers, tongue, eyelids, IK pole targets) ride
 * their parents at their own rest pose, so they follow the body without
 * being animated by data that does not exist for them.
 *
 * Runs once at load and feeds the bone-texture bake, so nothing here costs
 * anything per frame. Zero sim coupling.
 */
import {
  AnimationClip, AnimationMixer, Object3D, Quaternion, Vector3, Matrix4,
  QuaternionKeyframeTrack, VectorKeyframeTrack, SkinnedMesh, Bone, Euler,
} from 'three';

/**
 * Target bone <- source bone, for everything that reads at corridor
 * distance. Fingers, tongue and eyelids are deliberately absent: the
 * Quaternius rig has no wrist bone at all (its finger chains hang off
 * LowerArm), so there is nothing sensible to map a Mixamo hand onto, and
 * a mis-mapped hand is far more visible than an unanimated one.
 */
export const QUATERNIUS_FROM_MIXAMO: Record<string, string> = {
  'Hips': 'Hips',
  'Abdomen': 'Spine',
  'Torso': 'Spine1',
  'Neck': 'Neck',
  'Head': 'Head',
  'Shoulder.L': 'LeftShoulder',
  'UpperArm.L': 'LeftArm',
  'LowerArm.L': 'LeftForeArm',
  'Shoulder.R': 'RightShoulder',
  'UpperArm.R': 'RightArm',
  'LowerArm.R': 'RightForeArm',
  'UpperLeg.L': 'LeftUpLeg',
  'LowerLeg.L': 'LeftLeg',
  'Foot.L': 'LeftFoot',
  'UpperLeg.R': 'RightUpLeg',
  'LowerLeg.R': 'RightLeg',
  'Foot.R': 'RightFoot',
};

/**
 * GLTFLoader SANITISES NODE NAMES, and both rigs lose characters to it.
 *
 * `Shoulder.L` arrives as `ShoulderL`; `mixamorig:Hips` arrives as
 * `mixamorigHips` with the colon gone, so a prefix pattern anchored on a
 * colon matches nothing. Matching on the raw authored names bound ZERO
 * bone pairs, silently — the retarget ran, emitted tracks for every bone,
 * and every one of them was the rest pose.
 *
 * So both sides are reduced to a canonical key: drop the Mixamo namespace
 * with or without its colon, then strip everything that is not a letter or
 * a digit and lowercase. `Shoulder.L`, `ShoulderL` and `shoulder_l` all
 * become `shoulderl`, and the map stops depending on which loader touched
 * the file last.
 */
/**
 * Emit the hip translation track at all.
 *
 * Off: a crowd body is placed by its instance matrix, and the only thing
 * this track adds is the clip's own vertical bob and lateral sway. That is
 * a real loss, but it is small — and it is the single most likely thing to
 * put a body through the deck when two rigs disagree about pelvis height.
 */
const HIP_TRANSLATION = false;

const canon = (n: string): string =>
  n.replace(/^mixamorig\d*:?/i, '').replace(/[^a-z0-9]/gi, '').toLowerCase();

function skeletonOf(root: Object3D): SkinnedMesh | null {
  let best: SkinnedMesh | null = null;
  let bestVerts = -1;
  root.traverse((o) => {
    const m = o as SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    const n = m.geometry?.getAttribute('position')?.count ?? 0;
    if (n > bestVerts) { bestVerts = n; best = m; }
  });
  return best;
}

/** Bones in hierarchy order — parents always before their children. */
function bonesInOrder(root: Object3D): Bone[] {
  const out: Bone[] = [];
  root.traverse((o) => { if ((o as Bone).isBone) out.push(o as Bone); });
  return out;
}

export interface RetargetReport {
  clips: AnimationClip[];
  /** How many bone pairs actually bound. Zero means the map is wrong. */
  mapped: number;
  /** Target bones the map named that this rig does not have. */
  missingTarget: string[];
  /** Source bones the map named that the clip rig does not have. */
  missingSource: string[];
  /**
   * Rest orientation of each mapped pair, in degrees, target vs source.
   *
   * Diagnostic, and it earned its place: two plausible causes for a body
   * that folds in half (a world-space hip translation, then a root-frame
   * mismatch) were both wrong, and both were guesses. This prints the
   * geometry instead of theorising about it.
   */
  restDeg: { bone: string; target: [number, number, number]; source: [number, number, number] }[];
}

/**
 * Retarget `clips` from `sourceRoot`'s skeleton onto `targetRoot`'s.
 *
 * @param map target bone name -> source bone name (source unprefixed)
 * @param fps sampling rate; Mixamo authors at 30, so 30 is lossless
 */
export function retarget(
  targetRoot: Object3D, sourceRoot: Object3D, clips: AnimationClip[],
  map: Record<string, string> = QUATERNIUS_FROM_MIXAMO, fps = 30,
): RetargetReport {
  const srcMesh = skeletonOf(sourceRoot);
  const dstMesh = skeletonOf(targetRoot);
  if (!srcMesh || !dstMesh) {
    return { clips: [], mapped: 0, missingTarget: [], missingSource: [], restDeg: [] };
  }

  // BOTH REST POSES ARE READ THE SAME WAY: place the root at identity and
  // read world matrices with NO clip applied. Deriving one from
  // `boneInverses` and the other from `matrixWorld` mixes bind space with
  // scene space, and any constant offset between them corrupts every
  // delta by exactly that offset.
  for (const r of [sourceRoot, targetRoot]) {
    r.position.set(0, 0, 0);
    r.quaternion.identity();
    r.scale.set(1, 1, 1);
    r.updateMatrixWorld(true);
  }

  const srcBones = new Map<string, Bone>();
  for (const b of bonesInOrder(sourceRoot)) srcBones.set(canon(b.name), b);
  const dstBones = bonesInOrder(targetRoot);
  const dstByName = new Map<string, Bone>();
  for (const b of dstBones) dstByName.set(canon(b.name), b);

  const missingTarget: string[] = [];
  const missingSource: string[] = [];
  /** dstBone -> srcBone, resolved. */
  const pairs = new Map<Bone, Bone>();
  for (const [dstName, srcName] of Object.entries(map)) {
    const d = dstByName.get(canon(dstName));
    const sBone = srcBones.get(canon(srcName));
    if (!d) { missingTarget.push(dstName); continue; }
    if (!sBone) { missingSource.push(srcName); continue; }
    pairs.set(d, sBone);
  }

  // Rest world rotations, and each target bone's rest LOCAL rotation for
  // the bones nothing maps onto.
  /**
   * World rotation of a bone, via DECOMPOSE rather than
   * `setFromRotationMatrix`.
   *
   * setFromRotationMatrix requires an UNSCALED rotation matrix — three's
   * own documentation says so — and a Blender-exported rig routinely
   * carries scale on the armature and on individual bones. Feeding it a
   * scaled matrix returns a quaternion that is not a rotation, which
   * corrupts every rest pose, every delta computed from one, and therefore
   * every frame of every retargeted clip. It is also completely silent:
   * the bodies just come out wrong.
   *
   * decompose() divides the scale out first, which is the whole
   * difference.
   */
  const _p = new Vector3(), _s = new Vector3();
  const worldQuat = (b: Bone): Quaternion => {
    const q = new Quaternion();
    b.matrixWorld.decompose(_p, q, _s);
    return q;
  };

  const srcRest = new Map<Bone, Quaternion>();
  for (const b of srcBones.values()) srcRest.set(b, worldQuat(b));
  const dstRest = new Map<Bone, Quaternion>();
  const dstRestLocal = new Map<Bone, Quaternion>();
  for (const b of dstBones) dstRest.set(b, worldQuat(b));
  for (const b of dstBones) {
    const p = b.parent as Bone | null;
    const parentRest = p && (p as Bone).isBone ? dstRest.get(p) : null;
    dstRestLocal.set(b, parentRest
      ? parentRest.clone().invert().multiply(dstRest.get(b)!)
      : dstRest.get(b)!.clone());
  }

  // Hip translation is scaled by the height ratio, or a tall rig driven by
  // a short rig's clip sinks into the deck and vice versa.
  const srcHip = srcBones.get(canon(map['Hips'] ?? 'Hips'));
  const dstHip = dstByName.get(canon('Hips'));
  const srcHipY = srcHip ? new Vector3().setFromMatrixPosition(srcHip.matrixWorld).y : 1;
  const dstHipY = dstHip ? new Vector3().setFromMatrixPosition(dstHip.matrixWorld).y : 1;
  const hipScale = srcHipY > 1e-5 ? dstHipY / srcHipY : 1;
  const srcHipRest = srcHip
    ? new Vector3().setFromMatrixPosition(srcHip.matrixWorld) : new Vector3();
  const dstHipRest = dstHip
    ? new Vector3().setFromMatrixPosition(dstHip.matrixWorld) : new Vector3();
  /**
   * A `.position` track is LOCAL to the bone's parent, not world.
   *
   * Writing the world hip position straight into the track displaced the
   * hips by whatever transform sat above them — on this rig, Root and Body
   * — which dragged the pelvis down and folded the body into a permanent
   * crouch. It only showed on LOCOMOTION, because walks and runs are the
   * clips with real hip translation in them; the impacts and attacks
   * barely move the pelvis and looked fine, which is exactly what made it
   * read as a bad choice of run clip rather than as a bug.
   */
  const dstHipParentInv = new Matrix4();
  if (dstHip && dstHip.parent) dstHipParentInv.copy(dstHip.parent.matrixWorld).invert();

  /**
   * FRAME ALIGNMENT — the reason the first working version folded every
   * body in half.
   *
   * A world-space delta only transfers between two rigs if those rigs
   * agree on which way is forward. They do not: Mixamo is authored Y-up
   * facing -Z, and these Quaternius bodies come out of Blender Z-up, so
   * their rest frames differ by roughly a quarter turn about X. Applying
   * Mixamo's "lean forward" straight onto that rig is a command to fold at
   * the waist, which is exactly what it did — verified by rendering the
   * SOURCE body beside the retargeted one playing the same clip at the
   * same instant: the source stood up, the goblin lay down.
   *
   * The delta therefore has to be CONJUGATED into the target's frame:
   *
   *   A       = targetHipsRest * sourceHipsRest⁻¹
   *   delta_t = A * delta_s * A⁻¹
   *
   * Hips are the reference because they are the one bone both rigs
   * unambiguously share, and because everything else hangs off them. If
   * the two rigs happen to agree, A is identity and this is a no-op.
   */
  const frameAlign = new Quaternion();
  if (srcHip && dstHip) {
    frameAlign.copy(dstRest.get(dstHip)!)
      .multiply(srcRest.get(srcHip)!.clone().invert());
  }
  const frameAlignInv = frameAlign.clone().invert();

  const mixer = new AnimationMixer(sourceRoot);
  const out: AnimationClip[] = [];

  const delta = new Quaternion();
  const want = new Quaternion();
  const tmp = new Quaternion();
  const posed = new Quaternion();
  const hipPos = new Vector3();

  for (const clip of clips) {
    const frames = Math.max(2, Math.round(clip.duration * fps));
    const times = new Float32Array(frames);
    const values = new Map<Bone, Float32Array>();
    for (const b of dstBones) values.set(b, new Float32Array(frames * 4));
    const hipTrack = new Float32Array(frames * 3);

    const action = mixer.clipAction(clip);
    mixer.stopAllAction();
    action.reset();
    action.play();

    for (let f = 0; f < frames; f++) {
      const t = (f / frames) * clip.duration;
      times[f] = t;
      mixer.setTime(t);
      sourceRoot.updateMatrixWorld(true);

      // World rotation each target bone SHOULD have this frame.
      const wantWorld = new Map<Bone, Quaternion>();
      for (const b of dstBones) {
        const src = pairs.get(b);
        if (src) {
          src.matrixWorld.decompose(_p, posed, _s);
          // What the clip did to this bone, in the SOURCE's world frame...
          delta.copy(posed).multiply(srcRest.get(src)!.clone().invert());
          // ...conjugated into the TARGET's frame before it is applied.
          delta.premultiply(frameAlign).multiply(frameAlignInv);
          want.copy(delta).multiply(dstRest.get(b)!);
        } else {
          // Unmapped: ride the parent at this bone's own rest pose.
          const p = b.parent as Bone | null;
          const pw = p && (p as Bone).isBone ? wantWorld.get(p) : null;
          if (pw) want.copy(pw).multiply(dstRestLocal.get(b)!);
          else want.copy(dstRest.get(b)!);
        }
        wantWorld.set(b, want.clone());
      }

      // World -> local, now that every parent's world is known.
      for (const b of dstBones) {
        const p = b.parent as Bone | null;
        const pw = p && (p as Bone).isBone ? wantWorld.get(p) : null;
        tmp.copy(pw ? pw.clone().invert().multiply(wantWorld.get(b)!) : wantWorld.get(b)!);
        const arr = values.get(b)!;
        arr[f * 4] = tmp.x; arr[f * 4 + 1] = tmp.y;
        arr[f * 4 + 2] = tmp.z; arr[f * 4 + 3] = tmp.w;
      }

      if (srcHip && dstHip) {
        // World displacement the clip applied, scaled to this rig's build,
        // added to the rest pose — then taken back into the parent's space,
        // which is the space the track is actually read in.
        hipPos.setFromMatrixPosition(srcHip.matrixWorld).sub(srcHipRest)
          .multiplyScalar(hipScale).add(dstHipRest)
          .applyMatrix4(dstHipParentInv);
        hipTrack[f * 3] = hipPos.x;
        hipTrack[f * 3 + 1] = hipPos.y;
        hipTrack[f * 3 + 2] = hipPos.z;
      }
    }
    action.stop();

    const tracks: (QuaternionKeyframeTrack | VectorKeyframeTrack)[] = [];
    for (const b of dstBones) {
      tracks.push(new QuaternionKeyframeTrack(
        `${b.name}.quaternion`, Array.from(times), Array.from(values.get(b)!)));
    }
    if (srcHip && dstHip && HIP_TRANSLATION) {
      tracks.push(new VectorKeyframeTrack(
        `${dstHip.name}.position`, Array.from(times), Array.from(hipTrack)));
    }
    out.push(new AnimationClip(clip.name, clip.duration, tracks));
  }

  const DEG = 180 / Math.PI;
  const restDeg = [...pairs.entries()].map(([d, srcB]) => {
    const te = new Euler().setFromQuaternion(dstRest.get(d)!, 'XYZ');
    const se = new Euler().setFromQuaternion(srcRest.get(srcB)!, 'XYZ');
    return {
      bone: d.name,
      target: [te.x * DEG, te.y * DEG, te.z * DEG].map((v) => +v.toFixed(1)) as [number, number, number],
      source: [se.x * DEG, se.y * DEG, se.z * DEG].map((v) => +v.toFixed(1)) as [number, number, number],
    };
  });
  return { clips: out, mapped: pairs.size, missingTarget, missingSource, restDeg };
}
